import React, { FC, useMemo, useRef, useState } from 'react';

import { type Entity } from '@column/api';
import { Icon } from '@column/column-ui-kit';
import { Button, Popover, PopoverContent, PopoverTrigger, cn } from '@column/ui';

import { ROUTE } from '~/app/routes';
import { useEntityOptions } from '~/components/fields/EntityDropdown';
import { useNavigate } from '~/lib/navigation';
import { type HighlightSuffixCandidate, getHighlightedSuffix } from '~/lib/search/highlight';

import {
  FADE_ONLY_MOTION,
  FadeSwap,
  OVERLAP_CELL,
  OVERLAP_GRID,
  POPOVER_CONTENT_CLASS,
  SelectedCard,
  type SelectedCardRow,
  type SelectorOption,
  SelectorSearch,
  SelectorTrigger,
  TRIGGER_HEIGHT_PX,
  formatStatus,
  highlightMatch,
  renderMatchFragment,
} from './primitives';

const HIDDEN_MATCH_CANDIDATES: HighlightSuffixCandidate[] = [
  { field: 'PERSON_DETAILS.EMAIL', label: 'EMAIL' },
  { field: 'PERSON_DETAILS.PHONE_NUMBER', label: 'PHONE' },
  { field: 'PERSON_DETAILS.SSN', label: 'SSN' },
  { field: 'BUSINESS_DETAILS.EIN', label: 'EIN' },
  { field: 'BUSINESS_DETAILS.WEBSITE', label: 'WEBSITE' },
  { field: 'ID', label: 'ID' },
];

const AVATAR_ICON_CLASS = '[--icon-size-x:20px] [--icon-size-y:20px]';

const isBusiness = (entity?: Entity) => entity?.type?.toUpperCase() === 'BUSINESS';

export interface EntitySelectorProps {
  value: string;
  onChange: (id: string) => void;
  onBlur: () => void;
  locked?: boolean;
  /** Shows an arrow on the selected entity's card that calls this, e.g. to open the entity. */
  onOpen?: () => void;
  openLabel?: string;
  hasError?: boolean;
  dataCy?: string;
}

export const EntitySelector: FC<EntitySelectorProps> = ({
  value,
  onChange,
  onBlur,
  locked,
  onOpen,
  openLabel,
  hasError,
  dataCy,
}) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const selectedOnClose = useRef(false);
  const { entities, activeEntity, activeEntityIsLoading, search, handleScrollEnd } = useEntityOptions({
    value,
    // A locked selector only shows its entity, so the searchable list is never opened.
    listEnabled: !locked,
  });

  const options: SelectorOption[] = useMemo(
    () =>
      entities.map((option) => {
        const matchedHidden = getHighlightedSuffix(option.search_metadata, HIDDEN_MATCH_CANDIDATES);
        return {
          id: option.id,
          primary: highlightMatch(option.name || 'Unnamed', query),
          secondary: matchedHidden ? (
            <span className="font-mono">
              <span className="text-muted-foreground/60">{matchedHidden.label} </span>
              {renderMatchFragment(matchedHidden.fragment)}
            </span>
          ) : isBusiness(option) ? (
            'Business'
          ) : (
            'Person'
          ),
        };
      }),
    [entities, query]
  );

  const entity = activeEntity ?? entities.find((e) => e.id === value);
  const rows: SelectedCardRow[] = entity
    ? [
        { label: 'Entity ID', value: entity.id, monospaced: true },
        { label: 'Verification status', value: formatStatus(entity.verification_status) },
      ]
    : [];

  const closePopover = () => {
    setOpen(false);
    setQuery('');
    search('');
  };

  const handleCreate = () => navigate(`${ROUTE.ENTITIES}/edit/person`);

  return (
    <div className={OVERLAP_GRID}>
      <SelectedCard
        className={OVERLAP_CELL}
        open={Boolean(value)}
        title={entity?.name || 'Entity'}
        subtitle={entity ? (isBusiness(entity) ? 'Business' : 'Person') : undefined}
        icon={
          isBusiness(entity) ? (
            <Icon.Business className={AVATAR_ICON_CLASS} />
          ) : (
            <Icon.User className={AVATAR_ICON_CLASS} />
          )
        }
        rows={rows}
        isLoading={!entity && activeEntityIsLoading}
        hasError={hasError}
        onClear={locked ? undefined : () => onChange('')}
        onOpen={onOpen}
        openLabel={openLabel}
      />
      <FadeSwap visible={!value} className={OVERLAP_CELL}>
        <div data-cy={dataCy}>
          <Popover
            open={open}
            onOpenChange={(next) => {
              setOpen(next);
              if (!next) {
                onBlur();
                setQuery('');
                search('');
              }
            }}
          >
            {/* A sibling rather than a child of the trigger, since buttons can't nest. */}
            <div className="relative">
              <PopoverTrigger asChild>
                <SelectorTrigger label="Select an entity" hasError={hasError} className="[&>span]:pr-20" />
              </PopoverTrigger>
              <Button
                variant="muted"
                size="xs"
                type="button"
                onClick={handleCreate}
                className={cn(
                  'absolute top-1/2 right-10 -translate-y-1/2 transition-opacity',
                  open && 'pointer-events-none opacity-0'
                )}
              >
                <Icon.Plus className="[--icon-size-x:12px] [--icon-size-y:12px]" />
                Create
              </Button>
            </div>
            <PopoverContent
              align="start"
              sideOffset={-TRIGGER_HEIGHT_PX}
              className={POPOVER_CONTENT_CLASS}
              {...FADE_ONLY_MOTION}
              onCloseAutoFocus={(event) => {
                // After a selection the trigger is hidden, so don't restore focus to it.
                if (selectedOnClose.current) {
                  selectedOnClose.current = false;
                  event.preventDefault();
                }
              }}
            >
              <SelectorSearch
                options={options}
                searchPlaceholder="Search businesses or people…"
                emptyMessage="No entities found"
                onSearch={(q) => {
                  setQuery(q);
                  search(q);
                }}
                onSelect={(id) => {
                  selectedOnClose.current = true;
                  onChange(id);
                  closePopover();
                }}
                onScrollEnd={handleScrollEnd}
                footer={
                  <div className="border-t border-gray-100 p-2 dark:border-white/[0.08]">
                    <Button variant="secondary" size="sm" type="button" className="w-full" onClick={handleCreate}>
                      <Icon.Plus className="[--icon-size-x:16px] [--icon-size-y:16px]" />
                      Create Entity
                    </Button>
                  </div>
                }
              />
            </PopoverContent>
          </Popover>
        </div>
      </FadeSwap>
    </div>
  );
};
